import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { Plus, Trash2, Search, ShoppingCart, Loader2, User, X } from "lucide-react";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import { useNotification } from "@/shared/hooks/useNotification";
import { useApiError } from "@/shared/hooks/useApiError";
import { useConfirm } from "@/shared/contexts/confirm-context";
import {
  useCreateWalkInOrder,
  useEffectiveDiscount,
  useWalkInLookup,
} from "../hooks/useWalkInOrders";
import { usePagedAvailableGreenhouseStock } from "../hooks/useGreenhouseStock";
import { StockSowingDate } from "../components/greenhouseStock/StockSowingDate";
import { useExpoStockEntries } from "../hooks/useExpoStock";
import { useActiveVarietyPricings } from "../hooks/useVarietyPricings";
import {
  CompanySeedlingCategorySelector,
  type SeedlingCategory,
} from "../components/shared/CompanySeedlingCategorySelector";
import { cropTypesApi } from "../services/cropTypesApi";
import { usePaymentMethods } from "@/features/paymentMethods/hooks/usePaymentMethods";
import { useActiveTransportRates } from "@/features/dispatch/hooks/useTransportRates";
import {
  formatPaymentMethodLabel,
  isMpesaType,
} from "@/features/paymentMethods/services/paymentMethodsApi";
import type { Customer } from "@/features/customers/services/customersApi";
import type { CreateWalkInOrderPayload, WalkInPaymentLinePayload } from "../services/walkInOrdersApi";
import type { AvailableGreenhouseStockDto } from "../services/sowingInstructionsApi";

/** "tray" = selling whole trays at per-tray price; "seedling" = selling individual seedlings */
type SaleMode = "tray" | "seedling";

type CartLine = {
  tempId: string;
  varietyPricingId: string;
  greenhouseStockEntryId: string;
  expoStockEntryId?: string | null;
  /** Always in the stock's native unit (trays). For seedling-mode, this is the tray fraction. */
  quantity: string;
  /** Set when saleMode === "seedling" — the user-entered seedling count. */
  seedlingQty: string;
  saleMode: SaleMode;
  trayType?: number | null;
};

// PricingMode enum mirror (matches backend FarmSmartERP.Common.Enums.PricingMode)
const PRICING_MODE_PER_TRAY = 1;
const PRICING_MODE_PER_SEEDLING = 2;

/**
 * Normalize tray type labels from both API styles:
 * - display labels ("210-hole", "442-hole")
 * - enum names ("TwoTenHole", "FourFortyTwoHole")
 */
const normalizeTrayTypeLabel = (s?: string | null): string | null => {
  if (!s) return null;
  const t = s.trim();
  if (!t) return null;
  if (/^two\s*ten\s*hole$/i.test(t) || /^twotenhole$/i.test(t)) return "210-hole";
  if (/^four\s*forty\s*two\s*hole$/i.test(t) || /^fourfortytwohole$/i.test(t)) return "442-hole";
  return t;
};

/** "210-hole"/"TwoTenHole" → 210, "442-hole"/"FourFortyTwoHole" → 442, null/undefined → null. */
const parseStockTrayType = (s?: string | null): number | null => {
  const normalized = normalizeTrayTypeLabel(s);
  if (!normalized) return null;
  const m = /^(\d+)/.exec(normalized);
  return m ? parseInt(m[1], 10) : null;
};

const physicalPiecesPerTray = (stock?: Pick<AvailableGreenhouseStockDto, "trayTypeInt" | "trayType"> | null): number | null =>
  stock?.trayTypeInt ?? parseStockTrayType(stock?.trayType) ?? null;

/** For a tray entry, find the per-seedling pricing for the same variety (trayType-agnostic). */
const findSeedlingPricing = (
  pricings: ReturnType<typeof useActiveVarietyPricings>["data"],
  cropTypeName: string,
  variety: string | null | undefined
) => {
  if (!pricings) return undefined;
  const norm = (s?: string | null) => (s ?? "").trim().toLowerCase();
  return pricings.find(
    (p) =>
      norm(p.cropTypeName) === norm(cropTypeName) &&
      norm(p.varietyName) === norm(variety) &&
      p.pricingMode === PRICING_MODE_PER_SEEDLING &&
      p.companySeedlingPrice != null &&
      p.companySeedlingPrice > 0
  );
};

const initialCustomerForm = {
  name: "",
  email: "",
  idNumber: "",
  county: "",
  town: "",
  road: "",
  physicalLocationName: "",
  gpsLocation: "",
};

/**
 * Walk-in fast-path page (#24, #29).
 *
 * Two-step flow:
 *   1. Customer — phone-first lookup. Found → reuse; new → required Name + Phone, optional rest.
 *   2. Cart — pick from "Available now" greenhouse stock; payment must equal cart total.
 *
 * The "Sell & dispatch" button creates customer (if new) + order + stock decrement +
 * dispatch + payment + receipt-consume in a single backend transaction.
 */
export const WalkInSalePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const notify = useNotification();
  const handleApiError = useApiError();
  const confirm = useConfirm();
  const { hasPermission } = useAuthStore();

  const canSellExpo = hasPermission("expo_stock.sell");
  const canViewGreenhouseStock = hasPermission("greenhouse_stock.view");
  const requestedSource = searchParams.get("source");
  const isExpoRoute = location.pathname === "/production/expo-sale";
  const expoOnlyMode = isExpoRoute || requestedSource === "expo" || (canSellExpo && !canViewGreenhouseStock);

  // ── Step 1 state — customer ─────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<"customer" | "cart">("customer");
  const [phoneInput, setPhoneInput] = useState("");
  const [matchedCustomer, setMatchedCustomer] = useState<Customer | null>(null);
  const [showNewCustomerExtras, setShowNewCustomerExtras] = useState(false);
  const [newCustomer, setNewCustomer] = useState(initialCustomerForm);
  const lookupMutation = useWalkInLookup();
  const lookupRan = lookupMutation.isSuccess || lookupMutation.isError;

  // ── Step 2 state — cart + payment ───────────────────────────────────────
  const [cart, setCart] = useState<CartLine[]>([]);
  /** Holds the in-progress typed value for each cart line's quantity input.
   * We only commit to cart state (and thus recalculate cartTotal / payment auto-fill)
   * once the user leaves the field (onBlur), so typing "10" doesn't auto-fill the
   * payment at "1" before the second digit lands. */
  const [draftQty, setDraftQty] = useState<Map<string, string>>(new Map());
  const [catalogueSearch, setCatalogueSearch] = useState("");
  const [debouncedCatalogueSearch, setDebouncedCatalogueSearch] = useState("");
  const [stockPage, setStockPage] = useState(1);
  const [companySeedlingCategory, setCompanySeedlingCategory] = useState<SeedlingCategory>("vegetable");
  const [walkInCropTypeFilter, setWalkInCropTypeFilter] = useState("");
  const [cropTypeLabels, setCropTypeLabels] = useState<Record<string, string>>({});
  const [cartStockRows, setCartStockRows] = useState<AvailableGreenhouseStockDto[]>([]);
  const [transportRateId, setTransportRateId] = useState("");
  const [manualTransportCost, setManualTransportCost] = useState("");
  /** Split-tender payment lines. A single Cash line is the common case; cashiers add
   * more rows (+ Add payment) to record e.g. Cash 6,000 + Mpesa 4,000 = 10,000.
   * The sum across rows must equal the cart total — walk-ins cannot carry a balance. */
  type PaymentLine = {
    tempId: string;
    paymentMethodAccountId: string;
    amount: string;       // string for the controlled input; parsed at submit
    mpesaReceipt: string; // only used when the chosen account is an Mpesa type
    amountTouched: boolean;
  };
  const [payments, setPayments] = useState<PaymentLine[]>([
    { tempId: "pay-0", paymentMethodAccountId: "", amount: "", mpesaReceipt: "", amountTouched: false },
  ]);
  const [notes, setNotes] = useState("");
  const [requestSpecialDiscount, setRequestSpecialDiscount] = useState(false);
  const [specialDiscountKind, setSpecialDiscountKind] = useState<"Percentage" | "FixedAmount">("Percentage");
  const [specialDiscountValue, setSpecialDiscountValue] = useState("");
  const [specialDiscountReason, setSpecialDiscountReason] = useState("");

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedCatalogueSearch(catalogueSearch.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [catalogueSearch]);

  // The same available-stock endpoint used by order creation applies filters before paging.
  const stockQuery = usePagedAvailableGreenhouseStock({
    readyForWalkIn: true,
    source: companySeedlingCategory === "agroforestry" ? "Agroforestry" : "Greenhouse",
    cropTypeId: walkInCropTypeFilter || undefined,
    search: debouncedCatalogueSearch || undefined,
    pageNumber: stockPage,
    // Match the normal-order stock picker without making this endpoint unbounded.
    // The API caps pages at 100 and still applies all filters/paging in the database.
    pageSize: 100,
    enabled: !expoOnlyMode && activeTab === "cart",
  });
  const expoEntriesQuery = useExpoStockEntries({ includeZeroBalance: false, enabled: expoOnlyMode });
  const pricingsQuery = useActiveVarietyPricings();
  const createMutation = useCreateWalkInOrder();
  const activeTransportRatesQuery = useActiveTransportRates();
  const activeTransportRates = activeTransportRatesQuery.data ?? [];
  const paymentMethodsQuery = usePaymentMethods();
  const paymentMethods = paymentMethodsQuery.data ?? [];
  const lineRequiresMpesaReceipt = (line: PaymentLine): boolean => {
    const method = paymentMethods.find((m) => m.id === line.paymentMethodAccountId);
    return method ? isMpesaType(method.type) : false;
  };
  const totalPaid = payments.reduce((sum, l) => {
    const v = parseFloat(l.amount);
    return sum + (isNaN(v) ? 0 : v);
  }, 0);

  // ── Derived ──────────────────────────────────────────────────────────────

  /**
   * Map varieties available now → matching VarietyPricing (or null when no price configured).
   *
   * Stock entries are unit-tagged: a 442-hole entry's AvailableQuantity is in 442-hole *trays*,
   * a PerSeedlingOrPiece entry's quantity is in *pieces*. Pricing rows are tagged the same way,
   * so match on (cropType, variety, trayType + pricingMode) to avoid quoting a per-seedling
   * price for tray stock (or 210-hole pricing for a 442-hole tray).
   *
   * Only ReadyForDispatch batches are returned by the API (readyForWalkIn=true).
   * For tray entries, we also search for a per-seedling pricing so the cashier can choose.
   */
  type WalkInStockRow = AvailableGreenhouseStockDto & { expoStockEntryId?: string | null };

  const catalogueStockRows = useMemo<WalkInStockRow[]>(() => {
    if (!expoOnlyMode) {
      return (stockQuery.data?.items ?? []).map((s) => ({ ...s, expoStockEntryId: null }));
    }

    return (expoEntriesQuery.data ?? [])
      .filter((e) => e.availableQuantity > 0)
      .map((e) => {
        const trayTypeInt = parseStockTrayType(e.trayType);
        const piecesPerTray = trayTypeInt ?? null;
        const availableSeedlings = piecesPerTray != null
          ? e.availableQuantity * piecesPerTray
          : e.availableQuantity;
        return {
          entryId: e.greenhouseStockEntryId,
          cropTypeId: e.cropTypeId,
          cropTypeName: e.cropTypeName,
          isAgroforestry: false,
          variety: e.variety ?? undefined,
          customerName: "Expo",
          trayType: normalizeTrayTypeLabel(e.trayType),
          availableQuantity: e.availableQuantity,
          totalQuantity: e.totalMovedQuantity,
          reservedQuantity: 0,
          fillingReserve: 0,
          stockUnit: e.stockUnit,
          greenhouseName: "Expo",
          bayName: null,
          bedNumber: null,
          chamberName: undefined,
          entryDate: e.firstMovedAt,
          expectedReadyDate: undefined,
          sowingDate: undefined,
          pricingMode: e.pricingMode.toLowerCase().includes("seedling")
            ? PRICING_MODE_PER_SEEDLING
            : PRICING_MODE_PER_TRAY,
          trayTypeInt,
          sellablePerTray: piecesPerTray,
          availableSeedlings,
          pendingDispatchQuantity: 0,
          source: "Expo",
          isReadyForDispatch: true,
          expoStockEntryId: e.id,
        };
      });
  }, [expoOnlyMode, stockQuery.data?.items, expoEntriesQuery.data]);

  // Cart entries remain usable when the catalogue moves to another page or filter.
  const stockRows = useMemo<WalkInStockRow[]>(() => {
    const rows = new Map<string, WalkInStockRow>(cartStockRows.map((s) => [s.entryId, { ...s, expoStockEntryId: null }]));
    for (const row of catalogueStockRows) rows.set(row.entryId, row);
    return [...rows.values()];
  }, [cartStockRows, catalogueStockRows]);

  const availableCatalogue = useMemo(() => {
    if (!pricingsQuery.data) return [];
    const norm = (s?: string | null) => (s ?? "").trim().toLowerCase();
    return catalogueStockRows
      .filter((s) => s.availableQuantity > 0)
      .map((s) => {
        const stockTray = parseStockTrayType(s.trayType);
        const expectedMode = stockTray === null
          ? PRICING_MODE_PER_SEEDLING
          : PRICING_MODE_PER_TRAY;
        // Primary (tray-type-matched) pricing
        const trayPricing = pricingsQuery.data!.find(
          (p) =>
            norm(p.cropTypeName) === norm(s.cropTypeName) &&
            norm(p.varietyName) === norm(s.variety) &&
            p.pricingMode === expectedMode &&
            (p.trayType ?? null) === stockTray &&
            p.companySeedlingPrice != null &&
            p.companySeedlingPrice > 0
        );
        // For tray stock, also look for a per-seedling pricing (same variety, mode=2, no trayType)
        const seedlingPricing = stockTray !== null
          ? findSeedlingPricing(pricingsQuery.data, s.cropTypeName, s.variety)
          : undefined;
        return { stock: s, stockTray, trayPricing, seedlingPricing };
      });
  }, [catalogueStockRows, pricingsQuery.data]);

  const fetchWalkInCropTypes = useCallback(
    async ({
      search,
      pageNumber,
      pageSize,
      isAgroforestry,
    }: {
      search: string;
      pageNumber: number;
      pageSize: number;
      isAgroforestry: boolean;
    }) => {
      const result = await cropTypesApi.getCropTypesPaged({
        pageNumber,
        pageSize,
        search: search.trim() || undefined,
        isActive: true,
        isAgroforestry,
        sortBy: "name",
        sortDirection: "asc",
      });

      setCropTypeLabels((current) => Object.assign({}, current,
        Object.fromEntries(result.items.map((ct) => [ct.id, ct.name]))));

      return {
        items: result.items.map((ct) => ({
          value: ct.id,
          label: ct.name,
          isAgroforestry: ct.isAgroforestry,
        })),
      };
    },
    []
  );

  const filteredCatalogue = useMemo(() => {
    const q = catalogueSearch.trim().toLowerCase();
    return availableCatalogue
      .filter(({ stock }) =>
        companySeedlingCategory === "agroforestry" ? stock.isAgroforestry : !stock.isAgroforestry
      )
      .filter(({ stock }) => !walkInCropTypeFilter || stock.cropTypeId === walkInCropTypeFilter)
      .filter(({ stock }) => {
        if (!expoOnlyMode || !q) return true;
        return `${stock.cropTypeName} ${stock.variety ?? ""} ${stock.trayType ?? ""}`
          .toLowerCase()
          .includes(q);
      });
  }, [availableCatalogue, companySeedlingCategory, walkInCropTypeFilter, catalogueSearch, expoOnlyMode]);

  const getEffectiveLineQuantity = (line: CartLine, field: "quantity" | "seedlingQty"): number => {
    const draft = draftQty.get(line.tempId);
    const source = draft != null ? draft : line[field];
    const parsed = parseFloat(source);
    return isNaN(parsed) ? 0 : parsed;
  };

  const cartTotal = useMemo(() => {
    return cart.reduce((sum, line) => {
      const pricing = pricingsQuery.data?.find((p) => p.id === line.varietyPricingId);
      if (!pricing || pricing.companySeedlingPrice == null) return sum;
      if (line.saleMode === "seedling") {
        const seedlings = getEffectiveLineQuantity(line, "seedlingQty");
        return sum + pricing.companySeedlingPrice * seedlings;
      }
      const qty = getEffectiveLineQuantity(line, "quantity");
      return sum + pricing.companySeedlingPrice * qty;
    }, 0);
  }, [cart, pricingsQuery.data, draftQty]);

  // Effective discount for a matched (existing) customer. New walk-in customers have no id yet
  // and therefore no discount. The backend applies and re-validates this same discount, so the
  // cashier must collect the discounted total — that's what `payableTotal` below represents.
  const discountQuery = useEffectiveDiscount(matchedCustomer?.id);
  const discountPct = discountQuery.data?.effectiveDiscountPercentage ?? 0;
  const discountAmount = useMemo(
    () => Math.round(cartTotal * (discountPct / 100) * 100) / 100,
    [cartTotal, discountPct]
  );
  const trayQtyForTransport = useMemo(() => {
    return cart.reduce((sum, line) => {
      const stock = stockRows.find((s) => s.entryId === line.greenhouseStockEntryId);
      if (line.saleMode === "seedling") {
        const piecesPerTray = physicalPiecesPerTray(stock);
        if (!piecesPerTray || piecesPerTray <= 0) return sum;
        const seedlings = getEffectiveLineQuantity(line, "seedlingQty");
        return sum + (seedlings / piecesPerTray);
      }
      return sum + getEffectiveLineQuantity(line, "quantity");
    }, 0);
  }, [cart, stockRows, draftQty]);
  const hasTransportSelection = useMemo(() => {
    const parsedManual = parseFloat(manualTransportCost);
    return transportRateId !== "" || (!isNaN(parsedManual) && parsedManual > 0);
  }, [transportRateId, manualTransportCost]);
  const transportCost = useMemo(() => {
    const parsedManual = parseFloat(manualTransportCost);
    if (!isNaN(parsedManual) && parsedManual > 0) return parsedManual;
    if (!transportRateId) return 0;
    const selectedRate = activeTransportRates.find((r) => r.id === transportRateId);
    if (!selectedRate) return 0;
    return selectedRate.costAmount * trayQtyForTransport;
  }, [manualTransportCost, transportRateId, activeTransportRates, trayQtyForTransport]);
  const payableTotal = useMemo(
    () => Math.round((cartTotal - discountAmount + transportCost) * 100) / 100,
    [cartTotal, discountAmount, transportCost]
  );

  // Build a user-friendly discount label based on source type
  const discountLabel = useMemo(() => {
    const discountSource = discountQuery.data?.discountSource;
    const discountDetails = discountQuery.data?.discountDetails;
    if (!discountSource) return "";
    if (discountSource === "GroupCampaign" || discountSource === "GlobalCampaign") {
      const campaignMatch = discountDetails?.match(/^([^(]+)/);
      const campaignName = campaignMatch ? campaignMatch[1].trim() : "Campaign";
      return `Campaign: ${campaignName}`;
    } else if (discountSource === "Individual") {
      return "Individual";
    } else if (discountSource === "GroupDefault") {
      return "Group Default";
    }
    return discountSource;
  }, [discountQuery.data]);

  // Auto-fill the amount in the single-payment case so the cashier doesn't have to retype
  // the cart total — the common-path shortcut. Gated on:
  //   * Exactly one payment line (split tender always types each leg explicitly)
  //   * That line's amount is still empty (never overwrite a value the cashier already typed)
  // The "use cart total" button next to the Amount input handles the rare case where the
  // cashier wants to reset after editing or after the cart changed.
  //
  // The lint rule's general advice (derive instead) doesn't fit because Amount is a
  // controlled input the cashier can also edit; we need to seed it, not derive it.
  useEffect(() => {
    // Wait for a real total — adding a cart line fires this effect before the cashier has
    // typed a quantity, so cartTotal is still 0. Filling "0.00" then would lock the no-
    // overwrite guard and prevent the real-total auto-fill from ever landing.
    if (cart.length === 0 || payableTotal <= 0 || draftQty.size > 0) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPayments((prev) => {
      if (prev.length !== 1) return prev;
      const sole = prev[0];
      if (sole.amountTouched) return prev;
      const target = payableTotal.toFixed(2);
      if (sole.amount === target) return prev;
      return [{ ...sole, amount: payableTotal.toFixed(2) }];
    });
  }, [payableTotal, cart.length, draftQty.size]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleLookup = async () => {
    if (!phoneInput) {
      notify.error("Enter a phone number first.");
      return;
    }
    setMatchedCustomer(null);
    lookupMutation.mutate(phoneInput, {
      onSuccess: (customer) => {
        if (customer) {
          setMatchedCustomer(customer);
          notify.success(`Found: ${customer.customerName}`);
          setActiveTab("cart");
        } else {
          notify.info("No customer found with that phone number — fill in the new-customer form.");
        }
      },
      onError: (err) => handleApiError(err),
    });
  };

  const handleAddToCart = (
    entryId: string,
    varietyPricingId: string,
    saleMode: SaleMode,
    trayType?: number | null,
    expoStockEntryId?: string | null
  ) => {
    const selectedStock = catalogueStockRows.find((s) => s.entryId === entryId);
    if (selectedStock && !expoOnlyMode) {
      setCartStockRows((prev) => prev.some((s) => s.entryId === entryId) ? prev : [...prev, selectedStock]);
    }
    setCart((prev) => [
      ...prev,
      {
        tempId: `cart-${prev.length}-${Date.now()}`,
        greenhouseStockEntryId: entryId,
        expoStockEntryId: expoStockEntryId ?? null,
        varietyPricingId,
        quantity: "",
        seedlingQty: "",
        saleMode,
        trayType: trayType ?? null,
      },
    ]);
  };

  const handleRemoveLine = (tempId: string) => {
    const remaining = cart.filter((l) => l.tempId !== tempId);
    setCart(remaining);
    setCartStockRows((rows) => rows.filter((s) => remaining.some((l) => l.greenhouseStockEntryId === s.entryId)));
    setDraftQty((prev) => { const next = new Map(prev); next.delete(tempId); return next; });
  };

  const handleQuantityChange = (tempId: string, value: string, field: "quantity" | "seedlingQty" = "quantity") => {
    setCart((prev) => {
      const line = prev.find((l) => l.tempId === tempId);
      if (!line) return prev;

      const stock = stockRows.find((s) => s.entryId === line.greenhouseStockEntryId);
      if (!stock) return prev.map((l) => (l.tempId === tempId ? { ...l, [field]: value } : l));

      // Tray-qty already reserved by OTHER lines on the same stock entry
      const otherTrayQty = prev
        .filter((l) => l.tempId !== tempId && l.greenhouseStockEntryId === line.greenhouseStockEntryId)
        .reduce((sum, l) => {
          if (l.saleMode === "seedling") return sum + (parseFloat(l.seedlingQty) || 0) / (physicalPiecesPerTray(stock) ?? 1);
          return sum + (parseFloat(l.quantity) || 0);
        }, 0);

      const availableTrays = stock.availableQuantity - otherTrayQty;

      const parsed = parseFloat(value);
      let clamped = value;
      if (!isNaN(parsed) && parsed > 0) {
        if (field === "seedlingQty") {
          const maxSeedlings = Math.floor(availableTrays * (physicalPiecesPerTray(stock) ?? 1));
          if (parsed > maxSeedlings) clamped = String(maxSeedlings);
        } else {
          if (parsed > availableTrays) clamped = String(availableTrays);
        }
      }

      return prev.map((l) => (l.tempId === tempId ? { ...l, [field]: clamped } : l));
    });
  };

  const validateBeforeSubmit = (): string | null => {
    if (!phoneInput) return "Phone is required.";
    if (!matchedCustomer && !newCustomer.name) return "Customer name is required for new walk-ins.";
    if (cart.length === 0) return "Add at least one item to the cart.";

    // Per-line basic validation
    for (const line of cart) {
      if (line.saleMode === "seedling") {
        const seedlings = getEffectiveLineQuantity(line, "seedlingQty");
        if (isNaN(seedlings) || seedlings <= 0) return "Every cart line needs a quantity greater than zero.";
      } else {
        const qty = getEffectiveLineQuantity(line, "quantity");
        if (isNaN(qty) || qty <= 0) return "Every cart line needs a quantity greater than zero.";
      }
    }

    // Aggregate demand per stock entry — mirrors the backend check so multiple lines on the
    // same batch (e.g. one tray + one seedling) cannot collectively exceed available stock.
    const demandByStock = new Map<string, number>();
    for (const line of cart) {
      const stock = stockRows.find((s) => s.entryId === line.greenhouseStockEntryId);
      const trayDemand = line.saleMode === "seedling"
        ? getEffectiveLineQuantity(line, "seedlingQty") / (physicalPiecesPerTray(stock) ?? 1)
        : getEffectiveLineQuantity(line, "quantity");
      demandByStock.set(line.greenhouseStockEntryId,
        (demandByStock.get(line.greenhouseStockEntryId) ?? 0) + trayDemand);
    }
    for (const [stockId, demand] of demandByStock) {
      const stock = stockRows.find((s) => s.entryId === stockId);
      if (stock && demand > stock.availableQuantity) {
        const piecesPerTray = physicalPiecesPerTray(stock);
        const available = piecesPerTray != null
          ? `${Math.floor(stock.availableQuantity * piecesPerTray).toLocaleString()} seedlings`
          : `${stock.availableQuantity} trays`;
        return `Quantity for ${stock.variety || stock.cropTypeName} exceeds available stock (${available} available).`;
      }
    }

    if (requestSpecialDiscount) {
      const value = Number(specialDiscountValue);
      const proposed = specialDiscountKind === "Percentage" ? Math.round(cartTotal * value) / 100 : value;
      if (!Number.isFinite(value) || value <= 0 || (specialDiscountKind === "Percentage" && value > 100)
        || proposed > cartTotal || proposed <= discountAmount)
        return "Special discount must exceed the current discount and cannot exceed the goods subtotal.";
      if (!specialDiscountReason.trim()) return "Enter a reason for the special discount.";
      return null;
    }

    // ── Payment-line validation (split-tender supported) ──────────────────
    if (payments.length === 0) return "Add at least one payment line.";
    const seenMpesaReceipts = new Set<string>();
    for (const line of payments) {
      if (!line.paymentMethodAccountId) return "Pick a payment method for every payment line.";
      const amt = parseFloat(line.amount);
      if (isNaN(amt) || amt <= 0)
        return "Every payment line needs an amount greater than zero.";
      if (lineRequiresMpesaReceipt(line) && !line.mpesaReceipt.trim())
        return "M-Pesa receipt number is required on every Mpesa payment line.";
      if (lineRequiresMpesaReceipt(line)) {
        const r = line.mpesaReceipt.trim().toUpperCase();
        if (seenMpesaReceipts.has(r))
          return `Mpesa receipt '${r}' is used on more than one payment line — each Mpesa leg needs its own receipt.`;
        seenMpesaReceipts.add(r);
      }
    }
    if (Math.abs(totalPaid - payableTotal) > 0.01)
      return `Sum of payment lines (${totalPaid.toFixed(2)}) must equal the amount payable (${payableTotal.toFixed(2)}).`;
    return null;
  };

  const handleSubmit = () => {
    // Double-click guard — between a click and the next render that flips the button's
    // disabled prop, a fast second click can sneak in and fire a second submit. The
    // mutation would happily run twice, creating a duplicate order + duplicate stock
    // reservation. Bail at the function level so this never reaches the network.
    if (createMutation.isPending) return;

    const validationError = validateBeforeSubmit();
    if (validationError) {
      notify.error(validationError);
      return;
    }

    const payload: CreateWalkInOrderPayload = {
      specialDiscountRequest: requestSpecialDiscount ? {
        kind: specialDiscountKind, value: Number(specialDiscountValue), reason: specialDiscountReason.trim(),
      } : null,
      existingCustomerId: matchedCustomer?.id ?? null,
      phone: phoneInput,
      newCustomerName: matchedCustomer ? undefined : newCustomer.name,
      email: matchedCustomer ? undefined : newCustomer.email || null,
      idNumber: matchedCustomer ? undefined : newCustomer.idNumber || null,
      county: matchedCustomer ? undefined : newCustomer.county || null,
      town: matchedCustomer ? undefined : newCustomer.town || null,
      road: matchedCustomer ? undefined : newCustomer.road || null,
      physicalLocationName: matchedCustomer ? undefined : newCustomer.physicalLocationName || null,
      gpsLocation: matchedCustomer ? undefined : newCustomer.gpsLocation || null,
      items: cart.map((c) => {
        if (c.saleMode === "seedling") {
          const stock = stockRows.find((s) => s.entryId === c.greenhouseStockEntryId);
          const seedlings = getEffectiveLineQuantity(c, "seedlingQty");
          const isTrayBasedStock = c.trayType != null;
          const piecesPerTray = physicalPiecesPerTray(stock) ?? 1;
          return {
            varietyPricingId: c.varietyPricingId,
            greenhouseStockEntryId: c.greenhouseStockEntryId,
            expoStockEntryId: c.expoStockEntryId ?? null,
            quantity: isTrayBasedStock
              ? seedlings / piecesPerTray  // tray-fraction for tray-based stock tracking
              : seedlings,                 // direct piece count for non-tray stock
            trayType: c.trayType ?? null,
            seedlingQuantity: isTrayBasedStock ? seedlings : null,
          };
        }
        return {
          varietyPricingId: c.varietyPricingId,
          greenhouseStockEntryId: c.greenhouseStockEntryId,
          expoStockEntryId: c.expoStockEntryId ?? null,
          quantity: getEffectiveLineQuantity(c, "quantity"),
          trayType: c.trayType ?? null,
          seedlingQuantity: null,
        };
      }),
      payments: requestSpecialDiscount ? [] : payments.map<WalkInPaymentLinePayload>((line) => ({
        paymentMethodAccountId: line.paymentMethodAccountId,
        amount: parseFloat(line.amount),
        mpesaReceiptNumber: lineRequiresMpesaReceipt(line) ? line.mpesaReceipt.trim() : null,
      })),
      transportRateId: transportRateId || null,
      manualTransportCost:
        manualTransportCost && !isNaN(parseFloat(manualTransportCost))
          ? parseFloat(manualTransportCost)
          : null,
      notes: notes || null,
    };

    confirm({
      title: requestSpecialDiscount ? "Create pending discount order?" : expoOnlyMode ? "Complete expo sale?" : "Complete walk-in sale?",
      description: requestSpecialDiscount
        ? "The order will reserve stock at the current price. Do not collect payment until the GM reviews the requested discount."
        : expoOnlyMode
        ? "The order will be recorded and payment auto-approved for Expo sale."
        : "The order will be recorded and payment submitted for Finance approval.",
      confirmLabel: "Complete sale",
      body: (
        <div className="space-y-1">
          <p className="flex justify-between">
            <span className="text-muted-foreground">Cart total</span>
            <span className="font-medium">KES {cartTotal.toFixed(2)}</span>
          </p>
          {discountAmount > 0 && (
            <p className="flex justify-between text-emerald-700">
              <span>
                Discount{discountLabel ? ` (${discountLabel})` : ""}
                {discountPct > 0 ? ` ${discountPct.toFixed(2)}%` : ""}
              </span>
              <span className="font-medium">− KES {discountAmount.toFixed(2)}</span>
            </p>
          )}
          {hasTransportSelection && (
            <p className="flex justify-between">
              <span className="text-muted-foreground">Transport Cost</span>
              <span className="font-medium">KES {transportCost.toFixed(2)}</span>
            </p>
          )}
          <p className="flex justify-between">
            <span className="text-muted-foreground">Amount payable</span>
            <span className="font-medium">KES {payableTotal.toFixed(2)}</span>
          </p>
          <p className="flex justify-between">
            <span className="text-muted-foreground">Total paid</span>
            <span className="font-medium">KES {totalPaid.toFixed(2)}</span>
          </p>
          <p className="flex justify-between">
            <span className="text-muted-foreground">Payment lines</span>
            <span className="font-medium">{payments.length}</span>
          </p>
        </div>
      ),
      onConfirm: () =>
        createMutation.mutateAsync(payload).then((order) => {
        notify.success(
          requestSpecialDiscount
            ? `Order ${order.orderNumber} created. Special discount awaits GM approval; payment has not been collected.`
            : expoOnlyMode
            ? `Order ${order.orderNumber} recorded — Expo payment was auto-approved.`
            : `Order ${order.orderNumber} recorded — payment is pending Finance approval. ` +
              `Finance will print the receipt and the order will move to Dispatch once approved.`
        );
        // Reset all form state so the next sale starts from a clean slate AND so a
        // post-success click of the submit button can't re-submit the previous order.
        // `navigate(...)` to the same path is a no-op in React Router (the component
        // stays mounted), so explicit reset is the real safety net.
        setActiveTab("customer");
        setPhoneInput("");
        setMatchedCustomer(null);
        setNewCustomer(initialCustomerForm);
        setShowNewCustomerExtras(false);
        setCart([]);
        setCartStockRows([]);
        setCatalogueSearch("");
        setDebouncedCatalogueSearch("");
        setStockPage(1);
        setWalkInCropTypeFilter("");
        setTransportRateId("");
        setManualTransportCost("");
        setPayments([
          { tempId: "pay-0", paymentMethodAccountId: "", amount: "", mpesaReceipt: "", amountTouched: false },
        ]);
        setNotes("");
        setRequestSpecialDiscount(false);
        setSpecialDiscountValue("");
        setSpecialDiscountReason("");
        navigate(expoOnlyMode ? "/production/expo-sale?source=expo" : "/production/walk-in-sale");
        }).catch((err) => {
          handleApiError(err);
          throw err;
        }),
    });
  };

  // ── Render ───────────────────────────────────────────────────────────────

  if ((expoOnlyMode && expoEntriesQuery.isLoading) || pricingsQuery.isLoading) {
    return <LoadingAnimation fullScreen />;
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{expoOnlyMode ? "Expo Sale" : "Walk-in Sale"}</h1>
        <p className="text-gray-500 mt-1">
          {expoOnlyMode
            ? "Expo-only selling mode — pick Expo stock, capture payment, and complete checkout."
            : "Customer at the gate — look up by phone, pick stock, take payment. Finance approves the payment and prints the receipt. Dispatch follows once approved."}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "customer" | "cart")} className="space-y-4">
        <TabsList>
          <TabsTrigger value="customer">1. Customer</TabsTrigger>
          <TabsTrigger value="cart">
            2. Cart {cart.length > 0 && <Badge className="ml-2">{cart.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* ── Step 1 — Customer ───────────────────────────────────────── */}
        <TabsContent value="customer">
          <Card>
            <CardHeader>
              <CardTitle>Customer</CardTitle>
              <CardDescription>
                Enter the phone number first. If we find a match, we'll reuse the customer record.
                Otherwise, fill in at least the name; everything else is optional.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Field>
                <FieldLabel htmlFor="phone">Phone number *</FieldLabel>
                <div className="flex gap-2">
                  <Input
                    id="phone"
                    placeholder="0712345678, 712345678 or +254712345678"
                    value={phoneInput}
                    onChange={(e) => {
                      setPhoneInput(e.target.value);
                      setMatchedCustomer(null);
                    }}
                  />
                  <Button onClick={handleLookup} disabled={lookupMutation.isPending}>
                    {lookupMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                    <span className="ml-2">Lookup</span>
                  </Button>
                </div>
              </Field>

              {matchedCustomer ? (
                <div className="rounded border bg-emerald-50 p-3">
                  <p className="font-medium text-emerald-800">
                    {matchedCustomer.customerName}
                    {matchedCustomer.isWalkIn && (
                      <Badge variant="secondary" className="ml-2 text-xs">Walk-in</Badge>
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {matchedCustomer.mobileNo1}
                    {matchedCustomer.email ? ` · ${matchedCustomer.email}` : ""}
                  </p>
                  {matchedCustomer.physicalLocationName && (
                    <p className="text-sm text-muted-foreground">{matchedCustomer.physicalLocationName}</p>
                  )}
                </div>
              ) : lookupRan && !lookupMutation.isPending ? (
                <div className="space-y-3 rounded border p-3 bg-muted/30">
                  <p className="text-sm font-medium">New walk-in customer</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field>
                      <FieldLabel htmlFor="newName">Name *</FieldLabel>
                      <Input
                        id="newName"
                        value={newCustomer.name}
                        onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                        placeholder="As they introduce themselves"
                      />
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="newEmail">Email</FieldLabel>
                      <Input
                        id="newEmail"
                        type="email"
                        value={newCustomer.email}
                        onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                        placeholder="Optional"
                      />
                    </Field>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowNewCustomerExtras((v) => !v)}
                  >
                    {showNewCustomerExtras ? "Hide" : "Add"} additional details (optional)
                  </Button>
                  {showNewCustomerExtras && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Field>
                        <FieldLabel htmlFor="newId">ID number</FieldLabel>
                        <Input
                          id="newId"
                          value={newCustomer.idNumber}
                          onChange={(e) => setNewCustomer({ ...newCustomer, idNumber: e.target.value })}
                        />
                      </Field>
                      <Field>
                        <FieldLabel htmlFor="newCounty">County</FieldLabel>
                        <Input
                          id="newCounty"
                          value={newCustomer.county}
                          onChange={(e) => setNewCustomer({ ...newCustomer, county: e.target.value })}
                        />
                      </Field>
                      <Field>
                        <FieldLabel htmlFor="newTown">Town</FieldLabel>
                        <Input
                          id="newTown"
                          value={newCustomer.town}
                          onChange={(e) => setNewCustomer({ ...newCustomer, town: e.target.value })}
                        />
                      </Field>
                      <Field>
                        <FieldLabel htmlFor="newRoad">Road</FieldLabel>
                        <Input
                          id="newRoad"
                          value={newCustomer.road}
                          onChange={(e) => setNewCustomer({ ...newCustomer, road: e.target.value })}
                        />
                      </Field>
                      <Field className="col-span-2">
                        <FieldLabel htmlFor="newLocation">Physical location</FieldLabel>
                        <Input
                          id="newLocation"
                          value={newCustomer.physicalLocationName}
                          onChange={(e) =>
                            setNewCustomer({ ...newCustomer, physicalLocationName: e.target.value })
                          }
                          placeholder="e.g. Kakamega — opposite Tuskys"
                        />
                      </Field>
                    </div>
                  )}
                </div>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Step 2 — Cart ───────────────────────────────────────────── */}
        <TabsContent value="cart" className="space-y-4">

          {/* Customer banner */}
          {(matchedCustomer || newCustomer.name) && (
            <div className="flex items-center gap-2 rounded border bg-muted/40 px-3 py-2 text-sm">
              <User className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="font-medium">
                {matchedCustomer ? matchedCustomer.customerName : newCustomer.name}
              </span>
              <span className="text-muted-foreground">·</span>
              <span className="text-muted-foreground">{phoneInput}</span>
              {matchedCustomer?.isWalkIn && (
                <Badge variant="secondary" className="text-xs ml-1">Walk-in</Badge>
              )}
            </div>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Available now</CardTitle>
              <CardDescription>
                Only <strong>ReadyForDispatch</strong> batches are shown — confirmed mature by
                the Growing Supervisor. Use <strong>By tray</strong> or <strong>By seedling</strong> where both prices are configured.
              </CardDescription>
            </CardHeader>
            <CardContent>
                  <div className="mb-3 space-y-3">
                    <CompanySeedlingCategorySelector
                      category={companySeedlingCategory}
                      onCategoryChange={(category) => { setCompanySeedlingCategory(category); setWalkInCropTypeFilter(""); setStockPage(1); }}
                      selectedValue={walkInCropTypeFilter}
                      selectedLabel={cropTypeLabels[walkInCropTypeFilter]}
                      onSelectedValueChange={(value) => { setWalkInCropTypeFilter(value); setStockPage(1); }}
                      fetchOptions={fetchWalkInCropTypes}
                      allowAllOption
                      allOptionLabel="All Crop Types"
                      required={false}
                    />
                    <Input
                      id="saleCatalogueSearch"
                      placeholder="Search crop, variety, or tray type..."
                      value={catalogueSearch}
                      onChange={(e) => { setCatalogueSearch(e.target.value); setStockPage(1); }}
                    />
                  </div>
                  {!expoOnlyMode && stockQuery.isError ? (
                    <p className="text-sm text-destructive">Could not load available stock. Try again.</p>
                  ) : !expoOnlyMode && (stockQuery.isFetching || catalogueSearch.trim() !== debouncedCatalogueSearch || stockQuery.isPlaceholderData) ? (
                    <p className="text-sm text-muted-foreground">Loading available stock…</p>
                  ) : filteredCatalogue.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {catalogueSearch.trim().length > 0 || walkInCropTypeFilter
                        ? "No batches match your search in this tab."
                        : "No batches are ready for dispatch in this tab."}
                    </p>
                  ) : (
                <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                  {filteredCatalogue.map(({ stock, stockTray, trayPricing, seedlingPricing }) => {
                    const hasTrayPricing = !!trayPricing;
                    const hasSeedlingPricing = !!seedlingPricing;
                    const noPrice = !hasTrayPricing && !hasSeedlingPricing;
                    const qtyUnit = stockTray === null ? "seedlings" : "trays";

                    const inCartTrayQty = cart
                      .filter((l) => l.greenhouseStockEntryId === stock.entryId)
                      .reduce((s, l) => {
                        if (l.saleMode === "seedling") {
                          const seeds = parseFloat(l.seedlingQty) || 0;
                          return s + seeds / (physicalPiecesPerTray(stock) ?? 1);
                        }
                        return s + (parseFloat(l.quantity) || 0);
                      }, 0);
                    const remainingQty = stock.availableQuantity - inCartTrayQty;
                    const piecesPerTray = physicalPiecesPerTray(stock);
                    const remainingSeedlings = piecesPerTray != null
                      ? remainingQty * piecesPerTray
                      : remainingQty;

                    return (
                      <div key={stock.entryId} className="rounded border p-2 text-sm space-y-2">
                        <div>
                          <p className="font-medium">
                            {stock.cropTypeName}
                            {stock.variety ? ` — ${stock.variety}` : ""}
                            <span className="ml-2 text-xs text-muted-foreground">{stock.trayType}</span>
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {stock.greenhouseName ?? stock.chamberName ?? "—"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Sowing Date: <StockSowingDate value={stock.sowingDate} source={stock.source} />
                          </p>
                          <p className="text-xs font-medium">
                            {inCartTrayQty > 0 ? (
                              <span className="text-amber-700">
                                {remainingQty.toFixed(remainingQty % 1 === 0 ? 0 : 2)} {qtyUnit} left
                                {piecesPerTray != null && (
                                  <span> · {Math.floor(remainingSeedlings).toLocaleString()} seedlings</span>
                                )}
                              </span>
                            ) : (
                              <span className="text-emerald-700">
                                {stock.availableQuantity} {qtyUnit}
                                {piecesPerTray != null && (
                                  <span> · {Math.floor(stock.availableQuantity * piecesPerTray).toLocaleString()} seedlings</span>
                                )}
                              </span>
                            )}
                          </p>
                          {piecesPerTray != null && (
                            <p className="text-xs text-muted-foreground">
                              {piecesPerTray} pieces/tray
                            </p>
                          )}
                          {stock.pendingDispatchQuantity > 0 && (
                            <p className="text-xs text-amber-700 font-medium">
                              ⚠ {stock.pendingDispatchQuantity} tray{stock.pendingDispatchQuantity !== 1 ? "s" : ""} in active dispatch — will be deducted on handover
                            </p>
                          )}
                          {noPrice && (
                            <p className="text-xs text-amber-700">No price configured — contact a manager.</p>
                          )}
                        </div>
                        {!noPrice && (
                          <div className="flex gap-2 flex-wrap">
                            {hasTrayPricing && trayPricing && stockTray !== null && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={remainingQty <= 0}
                                onClick={() => handleAddToCart(stock.entryId, trayPricing.id, "tray", stockTray, stock.expoStockEntryId)}
                                className="gap-1"
                              >
                                <Plus className="h-3 w-3" />
                                By tray
                                <span className="text-xs text-muted-foreground ml-1">
                                  KES {trayPricing.companySeedlingPrice?.toFixed(2)}/tray
                                </span>
                              </Button>
                            )}
                            {/*
                              Per-piece stock (no TrayType — Source=Agroforestry, or any
                              non-tray variety). The primary pricing match is already
                              per-seedling; render it as such instead of mislabelling
                              it as "By tray".
                            */}
                            {hasTrayPricing && trayPricing && stockTray === null && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={remainingQty <= 0}
                                onClick={() => handleAddToCart(stock.entryId, trayPricing.id, "seedling", null, stock.expoStockEntryId)}
                                className="gap-1"
                              >
                                <Plus className="h-3 w-3" />
                                By seedling
                                <span className="text-xs text-muted-foreground ml-1">
                                  KES {trayPricing.companySeedlingPrice?.toFixed(2)}/seedling
                                </span>
                              </Button>
                            )}
                            {hasSeedlingPricing && seedlingPricing && stockTray !== null && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={remainingQty <= 0}
                                onClick={() => handleAddToCart(stock.entryId, seedlingPricing.id, "seedling", stockTray, stock.expoStockEntryId)}
                                className="gap-1"
                              >
                                <Plus className="h-3 w-3" />
                                By seedling
                                <span className="text-xs text-muted-foreground ml-1">
                                  KES {seedlingPricing.companySeedlingPrice?.toFixed(2)}/seedling
                                </span>
                              </Button>
                            )}
                            {!hasTrayPricing && hasSeedlingPricing && seedlingPricing && stockTray === null && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={remainingQty <= 0}
                                onClick={() => handleAddToCart(stock.entryId, seedlingPricing.id, "seedling", null, stock.expoStockEntryId)}
                                className="gap-1"
                              >
                                <Plus className="h-3 w-3" />
                                Add · KES {seedlingPricing.companySeedlingPrice?.toFixed(2)}/seedling
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                  )}
                  {!expoOnlyMode && !stockQuery.isError && catalogueSearch.trim() === debouncedCatalogueSearch &&
                    !stockQuery.isPlaceholderData && (stockQuery.data?.totalCount ?? 0) > 0 && (
                    <div className="mt-4 flex items-center justify-between gap-3 text-sm">
                      <span className="text-muted-foreground">
                        Showing {(stockPage - 1) * (stockQuery.data?.pageSize ?? 100) + 1}–{Math.min(
                          stockPage * (stockQuery.data?.pageSize ?? 100),
                          stockQuery.data?.totalCount ?? 0
                        )} of {stockQuery.data?.totalCount} batches
                      </span>
                      {(stockQuery.data?.totalPages ?? 0) > 1 && (
                        <div className="flex gap-2">
                          <Button type="button" variant="outline" size="sm" disabled={stockPage <= 1 || stockQuery.isFetching}
                            onClick={() => setStockPage((page) => page - 1)}>Previous</Button>
                          <Button type="button" variant="outline" size="sm"
                            disabled={stockPage >= (stockQuery.data?.totalPages ?? 1) || stockQuery.isFetching}
                            onClick={() => setStockPage((page) => page + 1)}>Next</Button>
                        </div>
                      )}
                    </div>
                  )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Cart</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {cart.length === 0 ? (
                <p className="text-sm text-muted-foreground">Empty — pick something from the list above.</p>
              ) : (
                <div className="space-y-2">
                  {cart.map((line) => {
                    const stock = stockRows.find((s) => s.entryId === line.greenhouseStockEntryId);
                    const pricing = pricingsQuery.data?.find((p) => p.id === line.varietyPricingId);
                    const isSeedlingMode = line.saleMode === "seedling";
                    const displayQty = isSeedlingMode
                      ? getEffectiveLineQuantity(line, "seedlingQty")
                      : getEffectiveLineQuantity(line, "quantity");
                    const subtotal = pricing?.companySeedlingPrice != null
                      ? pricing.companySeedlingPrice * displayQty
                      : 0;
                    const maxQty = isSeedlingMode
                      ? (stock ? Math.floor(stock.availableQuantity * (physicalPiecesPerTray(stock) ?? 1)) : undefined)
                      : (stock?.availableQuantity ?? undefined);
                    return (
                      <div key={line.tempId} className="flex items-center gap-3 rounded border p-2">
                        <div className="flex-1 text-sm">
                          <p className="font-medium">
                            {stock?.cropTypeName} {stock?.variety ? `— ${stock.variety}` : ""}
                            {stock?.trayType && (
                              <span className="ml-2 text-xs text-muted-foreground">{stock.trayType}</span>
                            )}
                            <Badge variant={isSeedlingMode ? "secondary" : "outline"} className="ml-2 text-xs">
                              {isSeedlingMode ? "per seedling" : "per tray"}
                            </Badge>
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {isSeedlingMode
                              ? `Max: ${maxQty?.toLocaleString() ?? "—"} seedlings`
                              : `Max: ${stock?.availableQuantity ?? "—"} trays`
                            }{" · "}KES {pricing?.companySeedlingPrice?.toFixed(2) ?? "—"} / {isSeedlingMode ? "seedling" : "tray"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Sowing Date: <StockSowingDate value={stock?.sowingDate} source={stock?.source} />
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Input
                            type="number"
                            min="0"
                            max={maxQty}
                            step="1"
                            value={draftQty.get(line.tempId) ?? (isSeedlingMode ? line.seedlingQty : line.quantity)}
                            onChange={(e) =>
                              setDraftQty((prev) => new Map(prev).set(line.tempId, e.target.value))
                            }
                            onBlur={(e) => {
                              handleQuantityChange(line.tempId, e.target.value, isSeedlingMode ? "seedlingQty" : "quantity");
                              setDraftQty((prev) => { const next = new Map(prev); next.delete(line.tempId); return next; });
                            }}
                            className="w-24"
                            placeholder="0"
                          />
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {isSeedlingMode ? "seedlings" : "trays"}
                          </span>
                        </div>
                        <p className="text-sm font-mono w-28 text-right">
                          KES {subtotal.toFixed(2)}
                        </p>
                        <Button variant="ghost" size="sm" onClick={() => handleRemoveLine(line.tempId)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                  <Separator />
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Cart total</span>
                    <span className="font-mono">KES {cartTotal.toFixed(2)}</span>
                  </div>
                  {discountAmount > 0 && (
                    <div className="flex items-center justify-between text-sm text-emerald-700">
                      <span>
                        Discount{discountLabel ? ` (${discountLabel})` : ""}
                        {discountPct > 0 ? ` ${discountPct.toFixed(2)}%` : ""}
                      </span>
                      <span className="font-mono">− KES {discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  {discountQuery.data?.warning && (
                    <p className="text-xs text-amber-600">{discountQuery.data.warning}</p>
                  )}
                  {matchedCustomer && <div className="space-y-1 rounded-md border p-3 text-sm">
                    <p className="font-medium">Approved discounts</p>
                    {(discountQuery.data?.candidates ?? []).length === 0 && <p className="text-muted-foreground">No approved discount currently applies.</p>}
                    {(discountQuery.data?.candidates ?? []).map((candidate, index) => <div key={`${candidate.source}-${candidate.discountId ?? index}`} className="flex justify-between gap-2">
                      <span>{candidate.name} · {candidate.percentage}%</span>{candidate.selected && <Badge>Applied</Badge>}
                    </div>)}
                    <p className="text-xs text-muted-foreground">{discountQuery.data?.precedenceExplanation}</p>
                  </div>}
                  {hasPermission("discounts.request") && <div className="space-y-2 rounded-md border p-3">
                    <Button type="button" variant="outline" size="sm" onClick={() => setRequestSpecialDiscount(value => !value)}>
                      {requestSpecialDiscount ? "Remove special request" : "Request Special Discount"}
                    </Button>
                    {requestSpecialDiscount && <div className="space-y-2 text-sm">
                      <p className="text-amber-700">GM approval is required. No payment will be collected now.</p>
                      <Field><FieldLabel>Discount type</FieldLabel>
                        <Select value={specialDiscountKind} onValueChange={value => setSpecialDiscountKind(value as "Percentage" | "FixedAmount")}>
                          <SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Percentage">Percentage</SelectItem><SelectItem value="FixedAmount">Fixed amount</SelectItem></SelectContent>
                        </Select>
                      </Field>
                      <Field><FieldLabel>Requested value</FieldLabel><Input type="number" min="0" step="0.01" value={specialDiscountValue} onChange={event => setSpecialDiscountValue(event.target.value)} /></Field>
                      <Field><FieldLabel>Reason</FieldLabel><Textarea value={specialDiscountReason} onChange={event => setSpecialDiscountReason(event.target.value)} /></Field>
                      {Number(specialDiscountValue) > 0 && <div>
                        <p>Current payable goods: KES {(cartTotal - discountAmount).toFixed(2)}</p>
                        <p>Proposed payable goods: KES {(cartTotal - (specialDiscountKind === "Percentage" ? Math.round(cartTotal * Number(specialDiscountValue)) / 100 : Number(specialDiscountValue))).toFixed(2)}</p>
                        <p>Additional reduction: KES {((specialDiscountKind === "Percentage" ? Math.round(cartTotal * Number(specialDiscountValue)) / 100 : Number(specialDiscountValue)) - discountAmount).toFixed(2)}</p>
                      </div>}
                    </div>}
                  </div>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field>
                      <FieldLabel htmlFor="walkinTransportRate">Transport rate (optional)</FieldLabel>
                      <Select
                        value={transportRateId || "none"}
                        onValueChange={(value) => setTransportRateId(value === "none" ? "" : value)}
                        disabled={activeTransportRatesQuery.isLoading}
                      >
                        <SelectTrigger id="walkinTransportRate" className="w-full">
                          <SelectValue placeholder={activeTransportRatesQuery.isLoading ? "Loading..." : "Customer collects (no transport)"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Customer collects (no transport)</SelectItem>
                          {activeTransportRates.map((rate) => (
                            <SelectItem key={rate.id} value={rate.id}>
                              {rate.destinationName} - KES {rate.costAmount.toFixed(2)}/tray
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="walkinManualTransport">Manual transport (KES)</FieldLabel>
                      <Input
                        id="walkinManualTransport"
                        type="number"
                        min="0"
                        step="0.01"
                        value={manualTransportCost}
                        onChange={(e) => setManualTransportCost(e.target.value)}
                        placeholder="Optional"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Manual value overrides selected transport rate.
                      </p>
                    </Field>
                  </div>
                  {hasTransportSelection && (
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>Transport Cost</span>
                      <span className="font-mono">KES {transportCost.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm font-medium">
                    <span>Amount payable</span>
                    <span className="font-mono text-lg">KES {payableTotal.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {!requestSpecialDiscount && <Card>
            <CardHeader>
              <CardTitle>Payment</CardTitle>
              <CardDescription>
                Walk-in orders cannot have a balance. The sum across payment lines must equal
                the amount payable (cart total less discount plus any transport). Add a second
                line for split payments (e.g. Cash + M-Pesa).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {payments.map((line, idx) => {
                const showMpesaReceipt = lineRequiresMpesaReceipt(line);
                return (
                  <div
                    key={line.tempId}
                    className="rounded border p-3 space-y-3 bg-muted/20"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium text-muted-foreground">
                        Payment {idx + 1}{payments.length > 1 ? ` of ${payments.length}` : ""}
                      </p>
                      {payments.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setPayments((prev) => prev.filter((p) => p.tempId !== line.tempId))
                          }
                          aria-label={`Remove payment ${idx + 1}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Field>
                        <FieldLabel htmlFor={`pm-${line.tempId}`}>Method *</FieldLabel>
                        <Select
                          value={line.paymentMethodAccountId}
                          onValueChange={(v) =>
                            setPayments((prev) =>
                              prev.map((p) =>
                                p.tempId === line.tempId ? { ...p, paymentMethodAccountId: v } : p
                              )
                            )
                          }
                          disabled={paymentMethodsQuery.isLoading}
                        >
                          <SelectTrigger id={`pm-${line.tempId}`} className="w-full">
                            <SelectValue
                              placeholder={
                                paymentMethodsQuery.isLoading
                                  ? "Loading..."
                                  : paymentMethods.length === 0
                                  ? "No approved methods"
                                  : "Select Method"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {paymentMethods.map((method) => (
                                <SelectItem key={method.id} value={method.id}>
                                  {formatPaymentMethodLabel(method)}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field>
                        <FieldLabel htmlFor={`amt-${line.tempId}`}>
                          Amount *
                          {payments.length === 1 && cart.length > 0 && line.amount === "" && (
                            <button
                              type="button"
                              className="ml-2 text-xs text-blue-600 hover:underline"
                              onClick={() =>
                                setPayments((prev) =>
                                  prev.map((p) =>
                                    p.tempId === line.tempId
                                      ? { ...p, amount: payableTotal.toFixed(2), amountTouched: false }
                                      : p
                                  )
                                )
                              }
                            >
                              use amount payable
                            </button>
                          )}
                        </FieldLabel>
                        <Input
                          id={`amt-${line.tempId}`}
                          type="number"
                          min="0"
                          step="0.01"
                          value={line.amount}
                          onChange={(e) =>
                            setPayments((prev) =>
                              prev.map((p) =>
                                p.tempId === line.tempId
                                  ? { ...p, amount: e.target.value, amountTouched: true }
                                  : p
                              )
                            )
                          }
                        />
                      </Field>
                    </div>
                    {showMpesaReceipt && (
                      <Field>
                        <FieldLabel htmlFor={`rcpt-${line.tempId}`}>M-Pesa receipt number *</FieldLabel>
                        <Input
                          id={`rcpt-${line.tempId}`}
                          value={line.mpesaReceipt}
                          onChange={(e) =>
                            setPayments((prev) =>
                              prev.map((p) =>
                                p.tempId === line.tempId
                                  ? { ...p, mpesaReceipt: e.target.value.toUpperCase() }
                                  : p
                              )
                            )
                          }
                          placeholder="e.g. RHK7LIXMQ4"
                        />
                      </Field>
                    )}
                  </div>
                );
              })}

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setPayments((prev) => [
                    ...prev,
                    {
                      tempId: `pay-${prev.length}-${Date.now()}`,
                      paymentMethodAccountId: "",
                      amount: "",
                      mpesaReceipt: "",
                      amountTouched: false,
                    },
                  ])
                }
              >
                <Plus className="h-4 w-4 mr-1" />
                Add payment
              </Button>

              {/* Sum-vs-total indicator (real-time) */}
              {cart.length > 0 && (() => {
                const remaining = payableTotal - totalPaid;
                const balanced = Math.abs(remaining) < 0.01;
                return (
                  <div
                    className={`flex items-center justify-between rounded border px-3 py-2 text-sm ${
                      balanced
                        ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                        : "border-amber-300 bg-amber-50 text-amber-800"
                    }`}
                  >
                    <span className="font-medium">
                      {balanced
                        ? "Payment matches amount payable"
                        : remaining > 0
                        ? "Remaining to collect"
                        : "Over the amount payable by"}
                    </span>
                    <span className="font-mono">
                      KES {Math.abs(balanced ? 0 : remaining).toFixed(2)}
                    </span>
                  </div>
                );
              })()}

              <Field>
                <FieldLabel htmlFor="notes">Notes</FieldLabel>
                <Input
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional"
                />
              </Field>
            </CardContent>
          </Card>}

          <div className="flex justify-end">
            <Button
              size="lg"
              onClick={handleSubmit}
              disabled={createMutation.isPending}
              className="min-w-[200px]"
            >
              {createMutation.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <ShoppingCart className="h-5 w-5" />
              )}
              <span className="ml-2">{requestSpecialDiscount ? "Create order for GM review" : "Create order & record payment"}</span>
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
